import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getMmlTileStats, getMmlTileLogs } from '../api/api';
import LogTable from '../components/admin/LogTable';

const TILE_LOG_COLUMNS = [
  { key: 'requested_at', label: 'Aika', style: { whiteSpace: 'nowrap' }, render: r => new Date(r.requested_at).toLocaleString('fi-FI') },
  { key: 'layer_name', label: 'Kerros' },
  { key: 'source', label: 'Lähde' },
  { key: 'tile_count', label: 'Ruudut', style: { textAlign: 'right' } },
  { key: 'cache_hit', label: 'Välimuisti', render: r => r.cache_hit == null ? '–' : r.cache_hit ? <span className="badge bg-success">Kyllä</span> : <span className="badge bg-warning text-dark">Ei</span> },
  { key: 'latency_ms', label: 'ms', style: { textAlign: 'right' }, render: r => r.latency_ms ?? '–' },
  { key: 'user_id', label: 'Käyttäjä', style: { maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' } },
  { key: 'ip_address', label: 'IP' },
];

function TileLogFilters({ values, onApply }) {
  const [v, setV] = useState(values);
  useEffect(() => { setV(values); }, [JSON.stringify(values)]); // eslint-disable-line
  const set = (k, val) => setV(p => ({ ...p, [k]: val }));
  return (
    <div className="d-flex flex-wrap gap-2 align-items-end">
      <div><label className="form-label mb-1 small">Alku</label><input type="date" className="form-control form-control-sm" value={v.from || ''} onChange={e => set('from', e.target.value)} style={{ width: 140 }} /></div>
      <div><label className="form-label mb-1 small">Loppu</label><input type="date" className="form-control form-control-sm" value={v.to || ''} onChange={e => set('to', e.target.value)} style={{ width: 140 }} /></div>
      <div><label className="form-label mb-1 small">Kerros</label><input className="form-control form-control-sm" placeholder="taustakartta..." value={v.layer_name || ''} onChange={e => set('layer_name', e.target.value)} style={{ width: 140 }} /></div>
      <div><label className="form-label mb-1 small">Lähde</label>
        <select className="form-select form-select-sm" value={v.source || ''} onChange={e => set('source', e.target.value)} style={{ width: 110 }}>
          <option value="">Kaikki</option><option value="leaflet">leaflet</option><option value="pdf">pdf</option>
        </select>
      </div>
      <div><label className="form-label mb-1 small">Välimuisti</label>
        <select className="form-select form-select-sm" value={v.cache_hit ?? ''} onChange={e => set('cache_hit', e.target.value)} style={{ width: 110 }}>
          <option value="">Kaikki</option><option value="true">Kyllä</option><option value="false">Ei</option>
        </select>
      </div>
      <div><label className="form-label mb-1 small">IP</label><input className="form-control form-control-sm" placeholder="10.0..." value={v.ip_address || ''} onChange={e => set('ip_address', e.target.value)} style={{ width: 120 }} /></div>
      <div><label className="form-label mb-1 small">Käyttäjä</label><input className="form-control form-control-sm" placeholder="id..." value={v.user_id || ''} onChange={e => set('user_id', e.target.value)} style={{ width: 120 }} /></div>
      <button className="btn btn-sm btn-success" onClick={() => onApply(v)}>Hae</button>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => { const cleared = {}; setV(cleared); onApply(cleared); }}>Tyhjennä</button>
    </div>
  );
}

const PRESETS = [
  { label: '7 pv', days: 7 },
  { label: '30 pv', days: 30 },
  { label: '90 pv', days: 90 },
  { label: 'YTD', ytd: true },
];

function isoDate(d) { return d.toISOString().slice(0, 10); }
function todayStr() { return isoDate(new Date()); }
function daysAgoStr(n) { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); }
function yearStartStr() { const d = new Date(); d.setMonth(0, 1); return isoDate(d); }

function fmtNum(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + ' M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + ' k';
  return String(n ?? 0);
}

function fmtEur(n) {
  return (n ?? 0).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
}

export default function MmlTileStats({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Read state from URL (prefix: mml_)
  const subTab    = searchParams.get('mml_subtab') || 'stats';
  const urlFrom   = searchParams.get('mml_from')   || daysAgoStr(30);
  const urlTo     = searchParams.get('mml_to')     || todayStr();
  const urlGran   = searchParams.get('mml_gran')   || 'day';
  const urlPreset = searchParams.get('mml_preset');

  // Local form state — mirrors URL values, allows editing before "Hae"
  const [from, setFrom]           = useState(urlFrom);
  const [to, setTo]               = useState(urlTo);
  const [granularity, setGranularity] = useState(urlGran);
  const [activePreset, setActivePreset] = useState(urlPreset ? parseInt(urlPreset, 10) : 1);

  function setUrlParams(updates) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === '' || v == null) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }

  function setSubTab(v) { setUrlParams({ mml_subtab: v }); }

  const load = useCallback((f, t, g) => {
    setLoading(true);
    setError(null);
    getMmlTileStats({ from: f, to: t, granularity: g })
      .then(r => setData(r.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Load stats whenever URL stats-related params change
  useEffect(() => {
    if (subTab === 'stats') load(urlFrom, urlTo, urlGran);
  }, [urlFrom, urlTo, urlGran, subTab]); // eslint-disable-line

  function applyPreset(idx) {
    const p = PRESETS[idx];
    const newFrom = p.ytd ? yearStartStr() : daysAgoStr(p.days);
    const newTo = todayStr();
    const g = p.days === 7 ? 'day' : p.ytd ? 'month' : granularity;
    setFrom(newFrom); setTo(newTo); setGranularity(g); setActivePreset(idx);
    setUrlParams({ mml_from: newFrom, mml_to: newTo, mml_gran: g, mml_preset: idx });
  }

  function applyCustom() {
    setActivePreset(null);
    setUrlParams({ mml_from: from, mml_to: to, mml_gran: granularity, mml_preset: '' });
  }

  const r = data?.range;
  const ytd = data?.ytd;
  const proj = data?.projection;

  const inner = (
    <div>
      {!embedded && <><h3 className="mb-1 fw-bold">MML WMTS — karttaruutupyynnöt</h3><p className="text-muted small mb-4">Seuranta, kustannukset ja välimuistisäästöt</p></>}

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item"><button className={`nav-link ${subTab === 'stats' ? 'active' : ''}`} onClick={() => setSubTab('stats')}>Tilastot</button></li>
        <li className="nav-item"><button className={`nav-link ${subTab === 'logs' ? 'active' : ''}`} onClick={() => setSubTab('logs')}>Lokit</button></li>
      </ul>

      {subTab === 'logs' && <LogTable fetchFn={getMmlTileLogs} columns={TILE_LOG_COLUMNS} filters={TileLogFilters} paramPrefix="mml_l_" />}

      {subTab === 'stats' && <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
        {PRESETS.map((p, i) => (
          <button key={i} className={`btn btn-sm ${activePreset === i ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => applyPreset(i)}>{p.label}</button>
        ))}
        <div className="d-flex align-items-center gap-1 ms-2">
          <input type="date" className="form-control form-control-sm" style={{ width: 145 }} value={from} onChange={e => { setFrom(e.target.value); setActivePreset(null); }} />
          <span className="text-muted">–</span>
          <input type="date" className="form-control form-control-sm" style={{ width: 145 }} value={to} onChange={e => { setTo(e.target.value); setActivePreset(null); }} />
        </div>
        <select className="form-select form-select-sm" style={{ width: 120 }} value={granularity} onChange={e => { setGranularity(e.target.value); setActivePreset(null); }}>
          <option value="day">Päivittäin</option>
          <option value="week">Viikottain</option>
          <option value="month">Kuukausittain</option>
        </select>
        <button className="btn btn-sm btn-success" onClick={applyCustom}>Hae</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="text-muted mb-3">Ladataan...</div>}

      {data && <>
        <div className="row g-3 mb-4">
          <StatCard label="Ruudut yhteensä (valittu aika)" value={fmtNum(r?.total_tiles)} sub={`${r?.request_count ?? 0} pyyntöä`} color="#0d6efd" />
          <StatCard label="Laskutettavat ruudut" value={fmtNum(r?.billed_tiles)} sub="Välimuistiosumat poistettu" color="#dc3545" />
          <StatCard label="Välimuistiosumat" value={fmtNum(r?.cached_tiles)} sub={`${r?.cache_hit_rate_pct ?? 0} % osuma-aste`} color="#198754" />
        </div>

        <div className="row g-3 mb-4">
          <StatCard label="YTD laskutettavat" value={fmtNum(ytd?.billed_tiles)} sub={`Hintaluokka: ${ytd?.tier_label}`} color="#6f42c1" />
          <StatCard label="YTD kustannus" value={fmtEur(ytd?.actual_cost_eur)} sub={`sis. ALV ${fmtEur(ytd?.actual_cost_vat_eur)}`} color="#6f42c1" />
          <StatCard label="Vuosiennuste (kust.)" value={fmtEur(proj?.cost_eur)} sub={`sis. ALV ${fmtEur(proj?.cost_vat_eur)} · ${fmtNum(proj?.annual_billed_tiles)} ruutua`} color="#fd7e14" />
          <StatCard label="Säästö välimuistista" value={fmtEur(proj?.cache_savings_eur)} sub={`sis. ALV ${fmtEur(proj?.cache_savings_vat_eur)} · ilman välimuistia ${fmtEur(proj?.cost_no_cache_eur)}`} color="#198754" />
        </div>

        {data.time_series?.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title mb-3">Ruudut aikajanalla</h6>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.time_series} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtNum(v)} />
                  <Tooltip formatter={(v, name) => [fmtNum(v), name === 'billed_tiles' ? 'Laskutettavat' : 'Välimuistista']} labelFormatter={l => `Päivä: ${l}`} />
                  <Legend formatter={v => v === 'billed_tiles' ? 'Laskutettavat' : 'Välimuistista'} />
                  <Bar dataKey="billed_tiles" stackId="a" fill="#dc3545" />
                  <Bar dataKey="cached_tiles" stackId="a" fill="#198754" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="row g-3">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title mb-3">Kerrokset</h6>
                <table className="table table-sm table-hover mb-0">
                  <thead><tr><th>Kerros</th><th className="text-end">Ruudut</th><th className="text-end">Välimuistista</th></tr></thead>
                  <tbody>
                    {data.by_layer.map(l => (
                      <tr key={l.layer}>
                        <td><code style={{ fontSize: '0.8rem' }}>{l.layer}</code></td>
                        <td className="text-end">{fmtNum(l.tiles)}</td>
                        <td className="text-end text-success">{fmtNum(l.cached)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title mb-3">Lähde</h6>
                <table className="table table-sm table-hover mb-0">
                  <thead><tr><th>Lähde</th><th className="text-end">Ruudut</th></tr></thead>
                  <tbody>
                    {data.by_source.map(s => (
                      <tr key={s.source}>
                        <td>{s.source === 'pdf' ? 'PDF-kartta' : 'Karttanäkymä (Leaflet)'}</td>
                        <td className="text-end">{fmtNum(s.tiles)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.78rem' }}>
                  Hinnoittelu ilman ALV. ALV 25,5 % lisätään laskuun.
                  Hintaluokkien kiinteät vuosihinnat: 300 k → 247 €, 500 k → 293 €, 1 M → 414 €...
                </p>
              </div>
            </div>
          </div>
        </div>
      </>}
      </>}
    </div>
  );

  if (embedded) return inner;
  return <div className="container-fluid py-4" style={{ maxWidth: 1100 }}>{inner}</div>;
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="col-sm-6 col-xl-3">
      <div className="card h-100">
        <div className="card-body">
          <div className="text-muted small mb-1">{label}</div>
          <div className="fw-bold fs-4" style={{ color }}>{value}</div>
          {sub && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}
